import React, { useState } from "react";
import axios from "axios";
import {
    StandardModal,
    StandardModalActionGroup,
    StandardModalSection,
    StandardModalSectionTitle,
    Toast
} from "src/components";
import { API_URL } from "src/scenes/App";
import CheckIcon from "src/img/Green-Check.svg";
import DoubleArrowRight from "src/img/arrowRight.svg";
import {
    ArrowWrapper,
    ChangeLicenseButton,
    ChangeOverview,
    CurrentText,
    CurrentTierBadge,
    LicenseField,
    LicenseFieldGrid,
    LicenseFieldLabel,
    LicenseNote,
    LicenseReadonlyInput,
    ModalPrimaryButton,
    ModalSecondaryButton,
    TierCard,
    TierName,
    TierPrice,
    UserDetailsHeader,
} from "./ManageLicense.style";

const formatTierPrice = (tier, currency) => `${currency ? `${currency} ` : ""}$${Number(tier?.renewalCost || 0).toFixed(2)} (${tier?.renewalPeriod})`;
const fullName = (client) => `${client?.firstName || ""} ${client?.lastName || ""}`.trim();

const ChangeLicenseModal = ({ show, client, selectedTier, onClose, onConfirm, isSaving }) => {
    if (!show || !client || !selectedTier) return null;

    return (
        <StandardModal
            open={show}
            onClose={onClose}
            widthMode="large"
            title="Change License"
            description="Review and confirm the selected license change for this user. Once confirmed, the user's current license will be updated to the newly selected license tier for the selected subscription period."
            footer={(
                <StandardModalActionGroup $pushRight>
                    <ModalSecondaryButton onClick={onClose}>Cancel</ModalSecondaryButton>
                    <ModalPrimaryButton disabled={isSaving} onClick={onConfirm}>
                        {isSaving ? "Saving..." : "Confirm & Save"}
                    </ModalPrimaryButton>
                </StandardModalActionGroup>
            )}
        >
            <StandardModalSection>
                <LicenseFieldGrid>
                    <LicenseField>
                        <LicenseFieldLabel>Name</LicenseFieldLabel>
                        <LicenseReadonlyInput>{fullName(client) || "-"}</LicenseReadonlyInput>
                    </LicenseField>
                    <LicenseField>
                        <LicenseFieldLabel>Email</LicenseFieldLabel>
                        <LicenseReadonlyInput>{client?.email || "-"}</LicenseReadonlyInput>
                    </LicenseField>
                    <LicenseField>
                        <LicenseFieldLabel>Mobile</LicenseFieldLabel>
                        <LicenseReadonlyInput>{client?.phoneNumber || "-"}</LicenseReadonlyInput>
                    </LicenseField>
                </LicenseFieldGrid>
            </StandardModalSection>

            <StandardModalSection>
                <StandardModalSectionTitle>License Change Overview</StandardModalSectionTitle>
                <LicenseNote style={{ margin: 0, color: "#6A7387" }}>
                        Review the user&apos;s current license and the newly selected license before confirming the change. This helps ensure the correct license tier and subscription period are being applied.
                </LicenseNote>

                <ChangeOverview>
                    <TierCard current style={{ marginTop: 0 }}>
                        <div>
                            <TierName>
                                {client?.licenseTier?.tierName || "-"} License <CurrentText>(Current)</CurrentText>
                            </TierName>
                            <TierPrice>{formatTierPrice(client?.licenseTier, selectedTier?.renewalCurrency)}</TierPrice>
                        </div>
                        <img src={CheckIcon} width="24px" height="24px" alt="" />
                    </TierCard>
                    <ArrowWrapper>
                        <img src={DoubleArrowRight} width="52px" height="52px" alt="" />
                    </ArrowWrapper>
                    <TierCard style={{ marginTop: 0 }}>
                        <div>
                            <TierName>{selectedTier?.tierName} License</TierName>
                            <TierPrice>{formatTierPrice(selectedTier, selectedTier?.renewalCurrency)}</TierPrice>
                        </div>
                    </TierCard>
                </ChangeOverview>

                <LicenseNote>
                       *License changes take effect immediately and will apply for the remainder of the user’s current subscription period.
                       The new license fee will be automatically updated and applied at the user’s next subscription renewal date.
                       Depending on the selected license tier, the user may also be required to upload additional documents or information where applicable.
                </LicenseNote>
            </StandardModalSection>
        </StandardModal>
    );
};

export const ManageLicenseModal = ({
    show,
    client,
    licenseTiers,
    token,
    onClose,
    onLicenseTierChanged,
    refreshTable,
}) => {
    const [selectedTier, setSelectedTier] = useState(null);
    const [showChangeLicenseModal, setShowChangeLicenseModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    if (!show || !client) return null;

    const tiers = licenseTiers?.tiers || [];
    const currentTierUuid = client?.licenseTier?.uuid;
    const renewalCurrency = licenseTiers?.renewalCurrency;

    const openChangeLicenseModal = (tier) => {
        setSelectedTier({ ...tier, renewalCurrency });
        setShowChangeLicenseModal(true);
    };

    const closeChangeLicenseModal = () => {
        setShowChangeLicenseModal(false);
    };

    const closeModal = () => {
        setSelectedTier(null);
        setShowChangeLicenseModal(false);
        onClose();
    };

    const confirmLicenseTierChange = async () => {
        if (!client || !selectedTier) return;

        setIsSaving(true);
        try {
            const response = await axios.put(
                `${API_URL}/company/clientLicense/${client.employeeUuid}/licenseTier`,
                { licenseTierUuid: selectedTier.uuid },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            const updatedClient = response?.data?.licensedEmployee;
            if (updatedClient) {
                onLicenseTierChanged(updatedClient);
            }

            Toast.success("License tier has been updated.");
            closeModal();
            refreshTable();
        } catch (error) {
            Toast.error(error.getApiErrorDetails().message);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <StandardModal
                open={show && !showChangeLicenseModal}
                onClose={closeModal}
                widthMode="large"
                title="Manage License"
                description="Manage and update this user's assigned license tier, view their current subscription details, and switch between available license options available."
                footer={(
                    <StandardModalActionGroup $pushRight>
                        <ModalSecondaryButton onClick={closeModal}>Close</ModalSecondaryButton>
                    </StandardModalActionGroup>
                )}
            >
                <StandardModalSection>
                    <UserDetailsHeader>
                        <StandardModalSectionTitle style={{ margin: 0 }}>User Details</StandardModalSectionTitle>
                        <CurrentTierBadge>{client?.licenseTier?.tierName || "-"} License</CurrentTierBadge>
                    </UserDetailsHeader>
                    <LicenseFieldGrid>
                        <LicenseField>
                            <LicenseFieldLabel>First Name</LicenseFieldLabel>
                            <LicenseReadonlyInput>{client?.firstName || "-"}</LicenseReadonlyInput>
                        </LicenseField>
                        <LicenseField>
                            <LicenseFieldLabel>Last Name</LicenseFieldLabel>
                            <LicenseReadonlyInput>{client?.lastName || "-"}</LicenseReadonlyInput>
                        </LicenseField>
                        <LicenseField>
                            <LicenseFieldLabel>Company Name</LicenseFieldLabel>
                            <LicenseReadonlyInput>{client?.customCompanyName || "-"}</LicenseReadonlyInput>
                        </LicenseField>
                        <LicenseField>
                            <LicenseFieldLabel>Email</LicenseFieldLabel>
                            <LicenseReadonlyInput>{client?.email || "-"}</LicenseReadonlyInput>
                        </LicenseField>
                        <LicenseField>
                            <LicenseFieldLabel>Mobile</LicenseFieldLabel>
                            <LicenseReadonlyInput>{client?.phoneNumber || "-"}</LicenseReadonlyInput>
                        </LicenseField>
                        <LicenseField>
                            <LicenseFieldLabel>Referral Code</LicenseFieldLabel>
                            <LicenseReadonlyInput>{client?.licenseTier?.referralCode || "-"}</LicenseReadonlyInput>
                        </LicenseField>
                    </LicenseFieldGrid>
                </StandardModalSection>

                <StandardModalSection>
                    <StandardModalSectionTitle>{tiers.length} License Tiers Available</StandardModalSectionTitle>
                    {tiers.map(tier => {
                        const isCurrent = tier.uuid === currentTierUuid;
                        return (
                            <TierCard key={tier.uuid} current={isCurrent}>
                                <div>
                                    <TierName>
                                        {tier.tierName} License {isCurrent && <CurrentText>(Current)</CurrentText>}
                                    </TierName>
                                    <TierPrice>{formatTierPrice(tier, renewalCurrency)}</TierPrice>
                                </div>
                                {isCurrent ? (
                                    <img src={CheckIcon} width="24px" height="24px" alt="" />
                                ) : (
                                    <ChangeLicenseButton onClick={() => openChangeLicenseModal(tier)}>
                                            Change License
                                    </ChangeLicenseButton>
                                )}
                            </TierCard>
                        );
                    })}
                </StandardModalSection>
            </StandardModal>

            <ChangeLicenseModal
                show={showChangeLicenseModal}
                client={client}
                selectedTier={selectedTier}
                onClose={closeChangeLicenseModal}
                onConfirm={confirmLicenseTierChange}
                isSaving={isSaving}
            />
        </>
    );
};
